/**
 * Data Transformer utility for Satellite Talent Dashboard
 * Transforms CSV data into formats expected by dashboard charts
 */

/**
 * Transform offer data into candidates data format
 * @param {Array<Object>} offerData - Raw offer data from CSV
 * @returns {Array<Object>} - Transformed candidates data
 */
export const transformToCandidatesData = (offerData) => {
  // Debug: Check if the Google Apps Script fix is working
  if (offerData.length > 0) {
    const roleKeys = Object.keys(offerData[0]).filter(key => key.toLowerCase().includes('role'));
    console.log('=== GOOGLE APPS SCRIPT DEBUG ===');
    console.log('Role-related keys found:', roleKeys);
    console.log('Expected: keys with .1, .2, .3 suffixes');
    console.log('Actual result:', roleKeys.length <= 2 ? 'FAILED - Only basic role keys found' : 'SUCCESS - Multiple role keys with suffixes found');
    console.log('First record sample keys:', Object.keys(offerData[0]).slice(0, 10));
    
    // Show actual values in role columns for first few records
    console.log('=== ROLE VALUES SAMPLE ===');
    offerData.slice(0, 3).forEach((record, index) => {
      console.log(`Record ${index + 1}:`);
      roleKeys.forEach(key => {
        if (record[key] && record[key].trim()) {
          console.log(`  ${key}: "${record[key]}"`);
        }
      });
    });
  }
  
  return offerData.map(offer => {
    // Determine candidate stage based on offer data
    let stage = 'Offered';
    if (offer['Was the offer accepted?']?.includes('Yes')) {
      stage = 'Hired';
    } else if (offer['Was the offer accepted?']?.includes('No')) {
      stage = 'Rejected';
    }
    
    // Determine role based on various role columns
    // The Google Apps Script handles duplicate column names by adding .1, .2, .3, etc. (no .0 for first occurrence)
    const roleColumns = [
      'Which role was the offer for?', 
      'Which role was the offer for?.1',
      'Which role was the offer for?.2',
      'Which role was the offer for?.3',
      'Which role was the offer for?.4',
      'Which role was the offer for?.5',
      'Which role was the offer for?.6',
      'Which role was the offer for?.7',
      'Which role was the offer for?.8',
      'Other'
    ];
    
    // Check all role columns and use the first non-empty value
    let role = '';
    for (const col of roleColumns) {
      if (offer[col] && offer[col].trim() !== '') {
        role = offer[col].trim();
        console.log(`Found role "${role}" in column "${col}" for candidate ${offer['What is the candidate\'s name?'] || 'Unknown'}`);
        break;
      }
    }
    
    // If no role found in expected columns, check all keys for role-related data
    if (!role) {
      const allKeys = Object.keys(offer);
      const roleKey = allKeys.find(key => 
        key.toLowerCase().includes('role') && 
        offer[key] && 
        offer[key].trim() !== ''
      );
      if (roleKey) {
        role = offer[roleKey].trim();
        console.log(`Found role "${role}" in fallback column "${roleKey}" for candidate ${offer['What is the candidate\'s name?'] || 'Unknown'}`);
      }
    }
    
    // If still no role found, try to use department
    if (!role && offer['What category/department does the role fall into?']) {
      role = offer['What category/department does the role fall into?'];
      console.log(`Using department "${role}" as role for candidate ${offer['What is the candidate\'s name?'] || 'Unknown'} (no specific role found)`);
    }
    
    // Convert date strings to Date objects
    const pipelineDate = offer['When did the candidate enter your pipeline?'] ? 
      new Date(offer['When did the candidate enter your pipeline?']) : null;
    
    const offerDate = offer['When was the offer extended?'] ? 
      new Date(offer['When was the offer extended?']) : null;
    
    const startDate = offer['When will they start?'] ? 
      new Date(offer['When will they start?']) : null;
      
    // Calculate time to hire in days
    let timeToHire = 0;
    if (pipelineDate && offerDate) {
      const calculatedDays = Math.round((offerDate - pipelineDate) / (1000 * 60 * 60 * 24));
      // Only use calculated value if it's positive (offer date should be after pipeline date)
      if (calculatedDays > 0) {
        timeToHire = calculatedDays;
      } else {
        // Log data quality issue for debugging
        console.warn(`Invalid date sequence for candidate: Pipeline date (${pipelineDate.toISOString()}) is after offer date (${offerDate.toISOString()})`);
        // Try to use manual time to hire field as fallback
        timeToHire = parseInt(offer['Time to hire']) || 0;
      }
    } else {
      // Use manual field if dates are not available
      timeToHire = parseInt(offer['Time to hire']) || 0;
    }
    
    // Ensure timeToHire is never negative
    timeToHire = Math.max(0, timeToHire);
    
    // Extract skills from role and department
    const skillsArray = [];
    if (role) skillsArray.push(role);
    if (offer['What category/department does the role fall into?']) {
      skillsArray.push(offer['What category/department does the role fall into?']);
    }
    
    // Generate quarter from the row's submission timestamp (when the hire was logged),
    // not the offer-extended date. This matches how hires are counted in the raw sheet/Excel.
    const timestampDate = offer['Timestamp'] ? new Date(offer['Timestamp']) : null;
    const quarter = timestampDate ? 
      `Q${Math.floor((timestampDate.getMonth() + 3) / 3)} ${timestampDate.getFullYear()}` : 
      null;
    
    // Extract location information - only use actual location data without defaults
    const location = offer['Where was the candidate hired?'] || '';
    
    // Generate a simple feedback score (1-5) based on comments sentiment
    const feedbackScore = offer['Any last words or comments?'] ? 
      (offer['Any last words or comments?'].includes('Strong') || 
       offer['Any last words or comments?'].includes('great') || 
       offer['Any last words or comments?'].includes('excellent')) ? 5 :
      (offer['Any last words or comments?'].includes('good') || 
       offer['Any last words or comments?'].includes('positive')) ? 4 : 
      3 : null;
    
    return {
      id: `candidate-${offer['Timestamp']}-${offer['What is the candidate\'s name?']}`.replace(/[^a-zA-Z0-9-]/g, '-'),
      name: offer['What is the candidate\'s name?'] || 'Unknown',
      client: offer['Which client was the offer made for?'] || '',
      role: role,
      seniority: offer['What is the seniority of this candidate?'] || '',
      stage: stage,
      department: offer['What category/department does the role fall into?'] || '',
      linkedIn: offer['What is the candidate\'s LinkedIn profile?'] || '',
      source: offer['Where did you find this candidate?'] || '',
      sourceDetail: offer['Where did you source the candidate from?'] || '',
      pipelineDate: pipelineDate,
      offerDate: offerDate,
      signDate: offer['When did the candidate sign?'] ? new Date(offer['When did the candidate sign?']) : null,
      startDate: startDate,
      salary: {
        amount: parseFloat(offer['What salary was offered to the candidate?']) || 0,
        currency: offer['Which currency is the salary offered in?'] || 'EUR'
      },
      timeToHire: timeToHire,
      timeToFill: parseInt(offer['Time to fill']) || 0,
      comments: offer['Any last words or comments?'] || '',
      // Additional fields for charts
      skills: skillsArray.join(', '),
      quarter: quarter,
      location: location,
      feedbackScore: feedbackScore
    };
  });
};

/**
 * Transform feedback data into NPS data format
 * @param {Array<Object>} feedbackData - Raw feedback data from CSV
 * @returns {Array<Object>} - Transformed NPS data
 */
export const transformToNPSData = (feedbackData) => {
  // Validate that we have feedback data
  if (!feedbackData || feedbackData.length === 0) {
    console.warn('No feedback data provided for NPS transformation');
    return [];
  }
  
  console.log(`Transforming ${feedbackData.length} feedback records to NPS data`);
  
  return feedbackData.map(feedback => {
    // Extract date from timestamp to determine quarter
    const feedbackDate = feedback['Timestamp'] ? new Date(feedback['Timestamp']) : new Date();
    const quarter = `Q${Math.floor((feedbackDate.getMonth() + 3) / 3)} ${feedbackDate.getFullYear()}`;
    
    // Get email parts to identify client
    const emailParts = (feedback['Email Address'] || '').split('@');
    const domain = emailParts.length > 1 ? emailParts[1].split('.')[0] : '';
    
    // Extract scores - the NPS score is now the overall experience score
    const overallScore = parseInt(feedback['How would you rate your overall experience working with the Talent Partner mentioned above? (0 = terrible, 10 = awesome)']) || 0;
    const recommendScore = parseInt(feedback['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)']) || 0;
    
    // Handle malformed CSV column names - try multiple variations
    const getImprovementSuggestions = (feedback) => {
      // Try different possible column name variations due to CSV formatting issues
      const possibleKeys = [
        'Do you have any suggestions on how we can improve our service?',
        'Do you have any suggestions on how we can improve our service?\n',
        '"Do you have any suggestions on how we can improve our service?\n"'
      ];
      
      for (const key of possibleKeys) {
        if (feedback[key]) {
          return feedback[key];
        }
      }
      
      // Fallback: search for any key containing "suggestions" and "improve"
      const fallbackKey = Object.keys(feedback).find(key => 
        key.toLowerCase().includes('suggestions') && 
        key.toLowerCase().includes('improve')
      );
      
      return fallbackKey ? feedback[fallbackKey] : '';
    };
    
    const improvementText = getImprovementSuggestions(feedback);
    
    return {
      id: `feedback-${feedback['Timestamp']}`.replace(/[^a-zA-Z0-9-]/g, '-'),
      client: domain || 'Unknown',
      talentPartner: feedback['Which Talent Partner are you currently working with ?'] || '',
      quarter: quarter,
      averageScore: overallScore, // Use experience score for NPS calculations (original behavior)
      npsScore: overallScore, // Use experience score for NPS chart (original behavior)
      score: overallScore, // Use experience score as primary score (original behavior)
      value: overallScore, // Use experience score as value (original behavior)
      feedbackScore: overallScore, // Keep overall experience score for Feedback Score Chart
      experienceScore: overallScore, // Add experienceScore field for NPSSegmentDetail component
      recommendationScore: recommendScore, // Add explicit recommendationScore field
      individualScores: [overallScore, recommendScore], // Include both scores
      feedback: feedback['What have been the most valuable aspects of working with this Talent Partner?'] || '',
      suggestions: improvementText,
      valuableAspects: feedback['What have been the most valuable aspects of working with this Talent Partner?'] || '',
      improvementSuggestions: improvementText,
      timestamp: feedbackDate
    };
  });
};

/**
 * Transform offer data into salary data format
 * @param {Array<Object>} offerData - Raw offer data from CSV
 * @returns {Array<Object>} - Transformed salary data
 */
export const transformToSalaryData = (offerData) => {
  // Group by role and seniority
  const roleSeniorityMap = {};
  const result = [];
  
  // Debug log
  console.log(`Processing ${offerData.length} offer records for salary data`);
  
  offerData.forEach((offer) => {
    // Determine role - combine all potential role columns
    // The Google Apps Script handles duplicate column names by adding .1, .2, .3, etc. (no .0 for first occurrence)
    const roleColumns = [
      'Which role was the offer for?', 
      'Which role was the offer for?.1',
      'Which role was the offer for?.2',
      'Which role was the offer for?.3',
      'Which role was the offer for?.4',
      'Which role was the offer for?.5',
      'Which role was the offer for?.6',
      'Which role was the offer for?.7',
      'Which role was the offer for?.8',
    ];
    
    // Get first non-empty role value
    let role = roleColumns.map(col => offer[col]).filter(Boolean)[0] || '';
    
    // If no role found in specific columns, check if there's an 'Other' value in the form
    if (!role && offer['Other']) {
      role = offer['Other'];
    }
    
    // If still no role, try to use department
    if (!role) {
      role = offer['What category/department does the role fall into?'] || 'Unspecified Role';
    }
    
    // Get seniority 
    const seniority = offer['What is the seniority of this candidate?'] || 'Unknown';
    
    // Process salary - handle different formats
    let salary = 0;
    const salaryStr = offer['What salary was offered to the candidate?'];
    
    if (salaryStr) {
      // Remove any non-numeric characters except decimal point
      const cleanSalary = salaryStr.toString().replace(/[^0-9.]/g, '');
      salary = parseFloat(cleanSalary);
      
      // If the salary is suspiciously low (likely abbreviated in thousands)
      // For example, if someone entered "90" meaning 90k or 90,000
      if (salary > 0 && salary < 1000) {
        salary = salary * 1000; // Convert to thousands
      }
    }
    
    // Create a key for grouping
    const key = `${role}-${seniority}`;
    
    // Get currency
    const currency = offer['Which currency is the salary offered in?'] || 'EUR';
    
    // Add to role-seniority map
    if (!roleSeniorityMap[key]) {
      roleSeniorityMap[key] = {
        role: role,
        level: seniority,
        salaries: [],
        currency: currency,
        location: '' // Don't use a default location
      };
    }
    
    if (salary > 0) {
      roleSeniorityMap[key].salaries.push(salary);
    }
  });
  
  // Create sample data if we don't have enough real data
  if (Object.keys(roleSeniorityMap).length === 0) {
    console.log('No salary data found in CSV, using sample data');
    // Sample data for common roles
    result.push(
      { role: 'Software Engineer', level: 'Junior', location: 'Europe', minSalary: 45000, maxSalary: 65000, avgSalary: 55000, currency: 'EUR' },
      { role: 'Software Engineer', level: 'Mid', location: 'Europe', minSalary: 60000, maxSalary: 85000, avgSalary: 72500, currency: 'EUR' },
      { role: 'Software Engineer', level: 'Senior', location: 'Europe', minSalary: 80000, maxSalary: 120000, avgSalary: 95000, currency: 'EUR' },
      { role: 'Product Manager', level: 'Mid', location: 'Europe', minSalary: 65000, maxSalary: 90000, avgSalary: 77500, currency: 'EUR' },
      { role: 'Product Manager', level: 'Senior', location: 'Europe', minSalary: 85000, maxSalary: 130000, avgSalary: 105000, currency: 'EUR' },
      { role: 'UX Designer', level: 'Mid', location: 'Europe', minSalary: 55000, maxSalary: 75000, avgSalary: 65000, currency: 'EUR' },
      { role: 'UX Designer', level: 'Senior', location: 'Europe', minSalary: 70000, maxSalary: 95000, avgSalary: 82500, currency: 'EUR' }
    );
    return result;
  }
  
  // Transform grouped data into salary ranges
  result.push(...Object.values(roleSeniorityMap).map(group => {
    const salaries = group.salaries.sort((a, b) => a - b);
    const minSalary = salaries[0] || 0;
    const maxSalary = salaries[salaries.length - 1] || 0;
    const avgSalary = salaries.length > 0 ? salaries.reduce((sum, val) => sum + val, 0) / salaries.length : 0;
    
    return {
      role: group.role,
      level: group.level,
      location: '', // Don't use a default location
      minSalary: minSalary,
      maxSalary: maxSalary,
      avgSalary: avgSalary,
      currency: group.currency
    };
  }));
  
  console.log(`Generated ${result.length} salary data records`);
  return result;
}

/**
 * Transform weekly TP report form responses into a structured format
 * @param {Array<Object>} rawData - Raw rows from the Google Sheet
 * @returns {Array<Object>} - Transformed weekly report records
 */
export const transformToWeeklyReportsData = (rawData) => {
  if (!rawData || rawData.length === 0) {
    console.warn('No weekly reports data provided for transformation');
    return [];
  }

  console.log(`Transforming ${rawData.length} weekly report records`);

  return rawData.map((row, index) => {
    const timestamp = row['Timestamp'] ? new Date(row['Timestamp']) : null;
    const weekNumber = parseInt(row['Week number']) || 0;

    // TP name — column header: "Who are you?"
    const tpName = (row['Who are you?'] || '').trim();

    // Client — column header: "Who is your current client?"
    const client = (row['Who is your current client?'] || '').trim();

    // Numeric KPIs — find columns by partial match to handle long question text
    const getNum = (key) => {
      const directVal = row[key];
      if (directVal !== undefined) return parseInt(directVal) || 0;
      // Fallback: search for a key that starts with the provided prefix
      const matchKey = Object.keys(row).find(k => k.startsWith(key));
      return matchKey ? parseInt(row[matchKey]) || 0 : 0;
    };

    const openPositions = getNum('How many positions do you have open curre');
    const screeningCalls = getNum('How many screening calls did you have this');
    const passedAfterScreening = getNum('How many candidates passed through after s');
    const finalStages = getNum('How many final stages happened this week?');
    const offersAccepted = getNum('How many offers were accepted this week?');
    const offersRejected = getNum('How many offers were rejected this week?');

    // Text fields
    const blockers = (row['Any blockers?'] || '').trim();
    const achievements = (row['Any achievements?'] || '').trim();

    // Derived metrics
    const totalOffers = offersAccepted + offersRejected;
    const offerAcceptanceRate = totalOffers > 0 ? Math.round((offersAccepted / totalOffers) * 100) : null;
    const conversionRate = screeningCalls > 0 ? Math.round((passedAfterScreening / screeningCalls) * 100) : null;

    return {
      id: `weekly-${index}-${weekNumber}-${tpName}`.replace(/[^a-zA-Z0-9-]/g, '-'),
      timestamp,
      weekNumber,
      tpName,
      client,
      openPositions,
      screeningCalls,
      passedAfterScreening,
      finalStages,
      offersAccepted,
      offersRejected,
      blockers,
      achievements,
      totalOffers,
      offerAcceptanceRate,
      conversionRate,
    };
  }).filter(r => r.weekNumber > 0 || r.tpName);
};
